import { useState, Dispatch, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { FormData, Section } from '../types';
import { QuestionField } from './QuestionField';
import { evaluateVisibility } from '../questionnaire/conditionEvaluator';

interface FormSectionProps {
  section: Section;
  formData: FormData;
  setFormData: Dispatch<SetStateAction<FormData>>;
  isLastSection: boolean;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const FormSection = ({
  section,
  formData,
  setFormData,
  isLastSection,
  onNext,
  onBack,
  onSubmit,
  isSubmitting
}: FormSectionProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateSection = (): boolean => {
    const newErrors: Record<string, string> = {};

    section.questions
      .filter(question => evaluateVisibility(question.visibleWhen, formData))
      .forEach(question => {
        if (question.required) {
          const value = formData[question.id as keyof FormData];

          if (question.id.includes('Comment')) return;

          if (
            value === undefined ||
            value === null ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
          ) {
            newErrors[question.id] = 'This field is required.';
          }
        }
      });

    if (isLastSection && formData.privacyConsent !== true) {
      newErrors.privacyConsent = 'Please confirm that you have read the Privacy Notice.';
    }

    if (isLastSection && formData.crossBorderConsent !== true) {
      newErrors.crossBorderConsent = 'Please confirm the separate consent for cross-border access.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = () => {
    if (validateSection()) {
      if (isLastSection) {
        onSubmit();
      } else {
        onNext();
      }
    }
  };

  const handleChange = (id: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: ''
      }));
    }
  };

  const handlePrivacyConsentChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      privacyConsent: checked
    }));

    if (checked && errors.privacyConsent) {
      setErrors(prev => ({
        ...prev,
        privacyConsent: ''
      }));
    }
  };

  return (
    <div>
      <h2 className="section-title">{section.title}</h2>

      <div className="questions-list">
        {section.questions
          .filter(question => evaluateVisibility(question.visibleWhen, formData))
          .map(question => (
            <div key={question.id} className="question-item">
              <QuestionField
                question={question}
                value={formData[question.id as keyof FormData]}
                onChange={handleChange}
                error={errors[question.id]}
                formData={formData}
              />
            </div>
          ))}
      </div>

      {isLastSection && (
        <div className="form-group" style={{ marginTop: '2rem' }}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={formData.privacyConsent === true}
              onChange={e => handlePrivacyConsentChange(e.target.checked)}
              disabled={isSubmitting}
            />

            <span className="checkbox-text">
              I have read the{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Notice
              </Link>{' '}
              and agree to the processing of the information provided for the purpose of
              evaluating our China activities, identifying potential areas of support and
              contacting me regarding relevant services. *
            </span>
          </label>

          {errors.privacyConsent && (
            <div
              className="form-error"
              style={{
                color: '#d32f2f',
                fontSize: '0.875rem',
                marginTop: '0.25rem'
              }}
            >
              {errors.privacyConsent}
            </div>
          )}
        </div>
      )}

      {isLastSection && (
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={formData.crossBorderConsent === true}
              onChange={e => {
                setFormData(prev => ({
                  ...prev,
                  crossBorderConsent: e.target.checked
                }));
                
                if (e.target.checked && errors.crossBorderConsent) {
                  setErrors(prev => ({
                    ...prev,
                    crossBorderConsent: ''
                  }));
                }
              }}
              disabled={isSubmitting}
            />

            <span className="checkbox-text">
              I separately consent to personal information contained in my China Readiness Check submission being made accessible to Startup Factory International GmbH in Germany for the review, evaluation and follow-up of my potential China project. Further information is provided in the{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Notice
              </Link>.
            </span>
          </label>

          {errors.crossBorderConsent && (
            <div
              className="form-error"
              style={{
                color: '#d32f2f',
                fontSize: '0.875rem',
                marginTop: '0.25rem'
              }}
            >
              {errors.crossBorderConsent}
            </div>
          )}
        </div>
      )}

      <div className="form-actions">
        {section.id !== 'company-profile' && (
          <button
            type="button"
            className="btn btn-outline"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Back
          </button>
        )}

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNextClick}
          disabled={isSubmitting}
        >
          {isLastSection
            ? isSubmitting
              ? 'Submitting...'
              : 'Submit Assessment'
            : 'Continue'}
        </button>
      </div>

      <p className="form-text">* Required</p>
    </div>
  );
};